import { FieldValues } from "../models/FieldValues";
import { Image } from "../models/Image";
import { VsaaTag } from "../models/VsaaTag";

export interface FileNameValidationResult {
  isValid: boolean;
  error?: string;
  sanitizedName?: string;
}

export enum ImageUploadType {
  Chapter = "image/png,image/jpeg,image/jpg,image/webp",
  Activity = "image/png,image/gif,image/jpeg,image/jpg,image/webp,image/bmp,image/svg+xml,.pptx,.vsdx,.pdf,.gif",
}

/**
 * Validates filename against SharePoint restrictions
 */
export const validateSharePointFileName = (
  filename: string,
  t?: (key: string, options?: Record<string, unknown>) => string
): FileNameValidationResult => {
  if (!filename.trim()) {
    return {
      isValid: false,
      error: t ? t("fileNameRequired") : "fileNameRequired",
    };
  }

  const trimmedName = filename.trim();

  // Check for invalid characters
  const invalidChars = /[~"#%&*:<>?/\\{|}]/;
  if (invalidChars.test(trimmedName)) {
    return {
      isValid: false,
      error: t ? t("invalidFileNameChars") : "invalidFileNameChars",
    };
  }

  // Check for leading/trailing spaces
  if (trimmedName !== trimmedName.trim()) {
    return {
      isValid: false,
      error: t ? t("fileNameSpaces") : "fileNameSpaces",
    };
  }

  // Check length
  if (trimmedName.length > 255) {
    return {
      isValid: false,
      error: t ? t("fileNameTooLong") : "fileNameTooLong",
    };
  }

  // Check for reserved names
  const reservedNames = [
    "CON",
    "PRN",
    "AUX",
    "NUL",
    "COM1",
    "COM2",
    "COM3",
    "COM4",
    "COM5",
    "COM6",
    "COM7",
    "COM8",
    "COM9",
    "LPT1",
    "LPT2",
    "LPT3",
    "LPT4",
    "LPT5",
    "LPT6",
    "LPT7",
    "LPT8",
    "LPT9",
  ];

  const nameWithoutExtension = trimmedName.split(".")[0].toUpperCase();
  if (reservedNames.includes(nameWithoutExtension)) {
    return {
      isValid: false,
      error: t
        ? t("reservedFileName", { fileName: nameWithoutExtension })
        : `"${nameWithoutExtension}" is a reserved filename in SharePoint`,
    };
  }

  return {
    isValid: true,
    sanitizedName: sanitizeFileName(trimmedName),
  };
};

/**
 * Sanitizes filename to be SharePoint-compatible
 */
export const sanitizeFileName = (filename: string): string => {
  return filename
    .trim()
    .replace(/[~"#%&*:<>?/\\{|}]/g, "") // Remove invalid chars
    .replace(/\s+/g, "_") // Replace spaces with underscores
    .replace(/\.+$/g, "") // Remove trailing periods
    .replace(/^\.+/g, "") // Remove leading periods
    .substring(0, 255); // Limit length
};

/**
 * Validates image dimensions
 */
export const validateImageDimensions = async (
  file: File,
  maxWidth: number,
  maxHeight: number,
  t?: (key: string, options?: Record<string, unknown>) => string
): Promise<{
  isValid: boolean;
  width?: number;
  height?: number;
  error?: string;
}> => {
  if (!file.type.startsWith("image/")) {
    return { isValid: true }; // Non-images don't need dimension validation
  }

  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    bitmap.close();

    const isValid = width <= maxWidth && height <= maxHeight;

    return {
      isValid,
      width,
      height,
      error: isValid
        ? undefined
        : t
          ? t("largeImageErrorMessage", {
              width,
              height,
              maxWidth,
              maxHeight,
            })
          : `Image dimensions ${width}x${height} exceed the maximum allowed ${maxWidth}x${maxHeight}`,
    };
  } catch {
    return {
      isValid: false,
      error: t ? t("failedToReadImage") : "Failed to read image dimensions",
    };
  }
};

/**
 * Validates file type for specific image types (chapter vs activity)
 */
export const validateFileType = (
  file: File,
  imageType: "chapter" | "activity",
  t?: (key: string, options?: Record<string, unknown>) => string
): { isValid: boolean; error?: string } => {
  if (imageType === "chapter" && !file.type.startsWith("image/")) {
    return {
      isValid: false,
      error: t
        ? t("invalidFileType")
        : "Only image files are allowed for chapters",
    };
  }

  // Activity images can be any file type
  return { isValid: true };
};

/**
 * Validates file size for activity images. Only images and not other file types
 */
export const validateFileSize = (
  file: File,
  imageType: "chapter" | "activity",
  maxSizeInMB: number,
  t?: (key: string, options?: Record<string, unknown>) => string
): { isValid: boolean; error?: string } => {
  if (imageType === "activity" && file.type.startsWith("image/")) {
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      const sizeInMB = (file.size / (1024 * 1024)).toFixed(2);
      return {
        isValid: false,
        error: t?.("fileSizeTooLarge", {
          size: sizeInMB,
          maxSize: maxSizeInMB.toString(),
        }),
      };
    }
  }

  return { isValid: true };
};

/**
 * Unified error clearing function
 */
export const clearErrorsByType = (
  errors: string[],
  errorType: "title" | "fileName" | "file",
  t?: (key: string, options?: Record<string, unknown>) => string
): string[] => {
  switch (errorType) {
    case "title": {
      const titleRequiredError = t?.("titleRequired") || "titleRequired";
      return errors.filter(
        (error) =>
          !error.toLowerCase().includes("title") &&
          error !== titleRequiredError &&
          !error.includes("titleRequired")
      );
    }

    case "fileName": {
      const duplicateError =
        t?.("fileNameAlreadyExists") || "A file with this name already exists";
      return errors.filter(
        (error) =>
          !error.toLowerCase().includes("filename") &&
          !error.toLowerCase().includes("name") &&
          !error.toLowerCase().includes("required") &&
          !error.toLowerCase().includes("characters") &&
          !error.toLowerCase().includes("spaces") &&
          !error.toLowerCase().includes("reserved") &&
          error !== duplicateError &&
          !(t && error.includes(t("fileNameRequired"))) &&
          !(t && error.includes(t("invalidFileNameChars"))) &&
          !(t && error.includes(t("fileNameSpaces"))) &&
          !(t && error.includes(t("fileNameTooLong"))) &&
          !(t && error.includes(t("reservedFileName"))) &&
          !(t && error.includes(t("fileNameSpacesWarning")))
      );
    }

    case "file": {
      return errors.filter(
        (error) =>
          !(t && error.includes(t("fileRequired"))) &&
          !error.toLowerCase().includes("file") &&
          !error.toLowerCase().includes("image") &&
          !error.toLowerCase().includes("dimensions") &&
          !error.toLowerCase().includes("type")
      );
    }

    default:
      return errors;
  }
};

/**
 * Check if filename conflicts with existing names (exact match including extension)
 */
export const isFileNameDuplicate = (
  inputName: string,
  existingNames: string[],
  currentFileName?: string
): boolean => {
  const trimmedInput = inputName.trim().toLowerCase();

  return existingNames.some((existingName) => {
    const existingLower = existingName.toLowerCase();

    // Skip comparison with current file (exact match)
    if (currentFileName) {
      const currentLower = currentFileName.toLowerCase();
      if (existingLower === currentLower) {
        return false;
      }
    }

    // Only consider duplicate if full name (including extension) matches
    return trimmedInput === existingLower;
  });
};

/**
 * Handles file selection with comprehensive validation
 */
export const handleFileSelection = async (
  file: File,
  imageType: "chapter" | "activity",
  imageWidthLimit: number,
  imageHeightLimit: number,
  currentErrors: string[],
  t?: (key: string, options?: Record<string, unknown>) => string
): Promise<{
  updatedErrors: string[];
  fileError?: string;
  isValid: boolean;
}> => {
  // Clear ALL file-related errors from main errors array
  const updatedErrors = clearErrorsByType(currentErrors, "file", t);

  // Validate file size for activity images, not PDFs, PPTX, VSDX..
  const fileSizeValidation = validateFileSize(file, imageType, 2, t);
  if (!fileSizeValidation.isValid) {
    return {
      updatedErrors,
      fileError: fileSizeValidation.error!,
      isValid: false,
    };
  }

  // Validate file type
  const fileTypeValidation = validateFileType(file, imageType, t);
  if (!fileTypeValidation.isValid) {
    return {
      updatedErrors,
      fileError: fileTypeValidation.error!,
      isValid: false,
    };
  }

  // Validate image dimensions for chapter images
  if (imageType === "chapter" && file.type.startsWith("image/")) {
    const dimensionValidation = await validateImageDimensions(
      file,
      imageWidthLimit,
      imageHeightLimit,
      t
    );

    if (!dimensionValidation.isValid) {
      return {
        updatedErrors,
        fileError: dimensionValidation.error!,
        isValid: false,
      };
    }
  }

  // File is valid
  return {
    updatedErrors,
    isValid: true,
  };
};

/**
 * Helper function to determine if a file is an image based on URL, base64 data, or filename
 */
export const isImageFile = (url: string, fileName?: string): boolean => {
  // Check if it's a base64 image data URL
  if (url.startsWith("data:image/")) {
    return true;
  }

  const imageExtensions = [
    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
    ".bmp",
    ".webp",
    ".svg",
  ];
  const urlToCheck = fileName || url;

  return imageExtensions.some((ext) =>
    urlToCheck.toLowerCase().includes(ext.toLowerCase())
  );
};

/**
 * Helper function to check for activity image duplicates
 * Only considers files duplicate if full filename (including extension) matches exactly
 */
export const checkActivityDuplicate = (
  fileName: string,
  publishedActivityNames: string[]
): boolean => {
  const trimmedInput = fileName.trim().toLowerCase();

  return publishedActivityNames.some((existingName) => {
    const existingLower = existingName.toLowerCase();

    // Only consider duplicate if full name (including extension) matches
    // This allows "myimage.png" and "myimage.jpg" to coexist
    return trimmedInput === existingLower;
  });
};

/**
 * Comprehensive filename validation for both create and edit scenarios
 */
export const validateFileName = (
  fileName: string,
  publishedNames: string[] | undefined,
  imageType: "chapter" | "activity",
  t: (key: string) => string,
  currentFileName?: string // For edit scenarios - the original filename
): {
  isValid: boolean;
  errors: string[];
} => {
  const errors: string[] = [];

  // Check if filename is provided
  if (!fileName.trim()) {
    errors.push(t("fileNameRequired"));
    return { isValid: false, errors };
  }

  // Validate SharePoint filename format
  const fileNameValidation = validateSharePointFileName(fileName.trim(), t);
  if (!fileNameValidation.isValid) {
    errors.push(fileNameValidation.error!);
  }

  // Check for duplicates only if we have published names
  if (publishedNames && fileName.trim()) {
    // Only check for duplicates if the filename has changed from the original
    // Compare case-insensitively and trim both sides
    const currentFileNameTrimmed = currentFileName?.trim().toLowerCase();
    const newFileNameTrimmed = fileName.trim().toLowerCase();

    if (
      !currentFileNameTrimmed ||
      newFileNameTrimmed !== currentFileNameTrimmed
    ) {
      const isDuplicate =
        imageType === "chapter"
          ? isFileNameDuplicate(
              fileName.trim(),
              publishedNames,
              currentFileName
            )
          : checkActivityDuplicate(fileName.trim(), publishedNames);

      if (isDuplicate) {
        errors.push(t("fileNameAlreadyExists"));
      }
    }
  }

  return { isValid: errors.length === 0, errors };
};

/**
 * Unified function to handle filename changes with real-time validation
 */
export const handleFileNameChangeUnified = (
  newFileName: string,
  currentErrors: string[],
  publishedNames: string[] | undefined,
  imageType: "chapter" | "activity",
  t: (key: string) => string,
  currentFileName?: string
): {
  updatedErrors: string[];
  isValid: boolean;
} => {
  // Clear existing filename-related errors using unified function
  const nonFileNameErrors = clearErrorsByType(currentErrors, "fileName", t);

  // Validate the new filename
  const validation = validateFileName(
    newFileName,
    publishedNames,
    imageType,
    t,
    currentFileName
  );

  return {
    updatedErrors: [...nonFileNameErrors, ...validation.errors],
    isValid: validation.isValid,
  };
};

/**
 * File name population that extracts filename without extension
 */
export const populateFileNameFromFile = (
  file: File,
  currentFileName: string
): string => {
  // If user has already entered a filename, keep it
  if (currentFileName.trim()) {
    return currentFileName;
  }

  // Extract base name without extension
  return file.name.substring(0, file.name.lastIndexOf(".")) || file.name;
};

/**
 * Unified comprehensive validation for image forms (create and edit)
 */
export const validateImageFormUnified = async (
  formData: { title: string; name: string; file: File | null },
  imageType: "chapter" | "activity",
  publishedNames: string[] | undefined,
  imageWidthLimit: number,
  imageHeightLimit: number,
  t: (key: string) => string,
  currentFileName?: string // For edit scenarios
): Promise<{
  isValid: boolean;
  errors: string[];
}> => {
  const errors: string[] = [];

  // Validate title
  if (!formData.title.trim()) {
    errors.push(t("titleRequired"));
  }

  // Validate filename using unified validation
  const fileNameValidation = validateFileName(
    formData.name,
    publishedNames,
    imageType,
    t,
    currentFileName
  );
  errors.push(...fileNameValidation.errors);

  // Validate file
  if (formData.file) {
    if (imageType === "chapter") {
      // Validate image dimensions for chapter images
      try {
        const isValidImage = await validateImageDimensions(
          formData.file,
          imageWidthLimit,
          imageHeightLimit,
          t
        );
        if (!isValidImage.isValid) {
          errors.push(isValidImage.error!);
        }
      } catch {
        errors.push(t("invalidImageFile"));
      }
    }
  }

  return { isValid: errors.length === 0, errors };
};

// Helper to split filename into name and extension
export const splitFileName = (fileName: string) => {
  const lastDot = fileName.lastIndexOf(".");
  if (lastDot === -1) return { name: fileName, ext: "" };
  return {
    name: fileName.substring(0, lastDot),
    ext: fileName.substring(lastDot),
  };
};

const KNOWN_MIME_TYPE_TO_EXTENSION: Record<string, string> = {
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "pptx",
  "application/vnd.ms-powerpoint": "ppt",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.ms-visio.drawing.main+xml": "vsdx",
  "application/vnd.visio": "vsd",
  "application/pdf": "pdf",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/svg+xml": "svg",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/bmp": "bmp",
};

const IGNORED_FILE_TYPES = new Set([
  "unknown",
  "octet-stream",
  "application/octet-stream",
]);

const extractValidExtension = (fileNameOrSegment?: string | null): string => {
  if (!fileNameOrSegment) {
    return "";
  }

  const trimmed = fileNameOrSegment.trim();
  const lastDotIndex = trimmed.lastIndexOf(".");
  if (lastDotIndex <= 0 || lastDotIndex === trimmed.length - 1) {
    return "";
  }

  const extension = trimmed.slice(lastDotIndex + 1).toLowerCase();
  if (!/^[a-z0-9]{2,5}$/i.test(extension)) {
    return "";
  }

  return extension;
};

const extractExtensionFromUrl = (rawUrl?: string | null): string => {
  if (!rawUrl) {
    return "";
  }

  const trimmedUrl = rawUrl.trim();
  if (!trimmedUrl || /^data:/i.test(trimmedUrl)) {
    return "";
  }

  const withoutQueryOrHash = trimmedUrl.split(/[?#]/)[0];
  const lastSegment = withoutQueryOrHash.split(/[/\\]/).pop() ?? "";
  if (!lastSegment) {
    return "";
  }

  try {
    return extractValidExtension(decodeURIComponent(lastSegment));
  } catch {
    return extractValidExtension(lastSegment);
  }
};

export type MediaFileTypeSource = {
  fileType?: Image["fileType"] | null;
  fileName?: Image["fileName"] | null;
  webUrl?: Image["webUrl"] | null;
  url?: string | null;
};

/**
 * Resolves the lowercase short file extension token for a media item.
 */
export const getMediaFileTypeDisplay = (
  image: MediaFileTypeSource | null | undefined
): string => {
  if (!image) {
    return "";
  }

  const fileNameExtension = extractValidExtension(image.fileName);
  if (fileNameExtension) {
    return fileNameExtension;
  }

  const rawFileType = image.fileType?.trim().toLowerCase() ?? "";
  const normalizedFileType = rawFileType
    .split(";")[0]
    .trim()
    .replace(/^\.+/, "");

  if (normalizedFileType && !IGNORED_FILE_TYPES.has(normalizedFileType)) {
    const mappedMimeExtension =
      KNOWN_MIME_TYPE_TO_EXTENSION[normalizedFileType];
    if (mappedMimeExtension) {
      return mappedMimeExtension;
    }

    if (!normalizedFileType.includes("/")) {
      return normalizedFileType;
    }
  }

  const urlExtension = extractExtensionFromUrl(image.url ?? image.webUrl);
  if (urlExtension) {
    return urlExtension;
  }

  return "";
};

/**
 * Ensures an image filename includes its resolved file extension when available.
 */
export const resolveImageFileNameWithExtension = (
  image: MediaFileTypeSource | null | undefined
): string => {
  const trimmedFileName = image?.fileName?.trim() ?? "";
  if (!trimmedFileName) {
    return "";
  }

  if (/.+\.[a-z0-9]{2,5}$/i.test(trimmedFileName)) {
    return trimmedFileName;
  }

  const resolvedExtension = getMediaFileTypeDisplay(image);
  if (resolvedExtension) {
    return `${trimmedFileName}.${resolvedExtension}`;
  }

  return trimmedFileName;
};

/**
 * Prepares HTML for API submission by removing base64 src attributes.
 */
export const replaceImageSrcWithImageIds = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const imgElements = doc.querySelectorAll("img");

  imgElements.forEach((img) => {
    // Remove base64 src
    img.removeAttribute("src");
    // Keep data-image-id and data-version-id
  });

  return doc.body.innerHTML;
};

// Merges the images metadata fields with the metadata fields of the associated activities.
export const mergeImagesFieldsWithAssociatedActivitiesFields = (
  images: Image[]
): Image[] => {
  return images.map((image) => {
    const imageFields = image.fieldValues;

    const merged: FieldValues = {
      tags: imageFields?.tags ?? {},
      booleans: {
        ...(imageFields?.booleans ?? {}),
        active: image.isActive ?? true,
        archived: image.isArchived ?? false,
      },
      users: imageFields?.users ?? {},
      urls: imageFields?.urls ?? {},
    };

    (image.activities || []).forEach((act) => {
      if (!act.fieldValues) {
        return;
      }

      const activityFields = act.fieldValues;

      Object.entries(activityFields.tags ?? {}).forEach(([key, val]) => {
        if (!val || val.length === 0) {
          return;
        }

        const existing = merged.tags[key] ? merged.tags[key] : [];

        const combined = [...existing, ...val];
        const unique = Array.from(
          new Set(combined.map((x: VsaaTag) => JSON.stringify(x)))
        ).map((s: string) => JSON.parse(s) as VsaaTag);

        merged.tags[key] = unique;
      });
    });

    return {
      ...image,
      fieldValues: merged,
    };
  });
};
